import {UserSearchAutocomplete} from '@hconnect/common/components/formFields'
import {ControlledFormField} from '@hconnect/common/types'
import {Chip} from '@mui/material'
import {useState} from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {useFormInputLabelWithSuffix, useProcessWorkOrderUserSearch} from '../../hooks'

type User = {
  id: string
  name: string
}

type ProcessWorkOrdersUserSearchFormFieldProps<TField extends FieldValues> =
  ControlledFormField<TField> & {
    plantId: string
    pleaseStartTypingLabel?: string
  }

export const ProcessWorkOrdersUserSearchFormField = <TField extends FieldValues>({
  plantId,
  label,
  name,
  control,
  disabled,
  readOnly,
  optional,
  pleaseStartTypingLabel
}: ProcessWorkOrdersUserSearchFormFieldProps<TField>) => {
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined)

  const {data: userSuggestions, isFetching: isUsersLoading} = useProcessWorkOrderUserSearch(
    plantId,
    searchTerm
  )

  const fieldDisabled = disabled || readOnly
  const inputLabel = useFormInputLabelWithSuffix({label, optional})
  const dataTestId = `user-search-field-${name}`

  return (
    <Controller
      name={name}
      disabled={fieldDisabled}
      control={control}
      render={({field, fieldState}) => {
        const filterAvailableUsers = (suggestions, currentValues) =>
          ((suggestions as User[]) ?? []).filter(
            (user) => !(currentValues || []).find((assigned: User) => assigned.id === user.id)
          )

        return (
          <UserSearchAutocomplete
            multiple
            name={name}
            value={field.value ?? []}
            label={inputLabel}
            required={!optional}
            disabled={fieldDisabled}
            forcePopupIcon={false}
            helperText={fieldState.error?.message}
            error={!!fieldState.error?.message}
            disableCloseOnSelect={false}
            placeholder={pleaseStartTypingLabel}
            data-test-id={dataTestId}
            onChange={(_, newValue) => {
              if (!Array.isArray(newValue)) {
                field.onChange([])
                return
              }

              field.onChange(newValue)
            }}
            onInputChange={setSearchTerm}
            query={{
              data: filterAvailableUsers(userSuggestions, field.value),
              isFetching: isUsersLoading
            }}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option: User, index: number) => {
                const props = getTagProps({index})
                return (
                  <Chip
                    {...props}
                    variant="outlined"
                    size="small"
                    key={option.id}
                    label={option.name}
                    onDelete={props.onDelete}
                    data-test-id={`${dataTestId}-tag-${option.id}`}
                  />
                )
              })
            }
          />
        )
      }}
    />
  )
}
